import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { BAR_DELAY_MS, useDelayedFlag } from "@/lib/loading";
import { cn } from "@/lib/utils";

/**
 * A single app-wide indication that the app is busy.
 *
 * Deliberately global rather than per-screen. Route skeletons only cover the
 * *first* load of a query; once data is cached, React Query refetches in the
 * background and every screen sits there showing stale numbers with no hint
 * that fresher ones are on the way. That silent case is the one people read as
 * "nothing happened when I clicked".
 *
 * Mutations count too, so approving a sequence or saving a setting shows the
 * same signal without every button having to grow its own spinner.
 *
 * It is fixed to the viewport top rather than placed in the layout so it can
 * never reflow content — a bar that pushes the page down by 2px on every
 * refetch is its own kind of annoying.
 */
export function LoadingBar() {
  const busy = useIsFetching() + useIsMutating() > 0;
  const show = useDelayedFlag(busy, BAR_DELAY_MS);

  return (
    <div
      aria-hidden={!show}
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 transition-opacity duration-200",
        show ? "opacity-100" : "opacity-0",
      )}
    >
      {show ? (
        <div className="h-full w-full overflow-hidden">
          <div className="h-full w-2/5 rounded-r-full bg-primary animate-loading-bar" />
        </div>
      ) : null}
    </div>
  );
}
